import { useEffect, useState, useRef, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { debounce } from 'lodash';
import { SortDirection } from '../../../utils/constants/enums';
import {
  IPagedResult,
  IMembershipTypeDto,
  IMembershipTypeSearchRequest
} from '../../../utils/constants/interfaces';

// Define an empty result set for when no data is returned
const emptySearchResults: IPagedResult<IMembershipTypeDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

// Define hook
const useMembershipTypeLookup = (
  requireKeywords: boolean,
  onChange: (membershipTypeId: string) => void
) => {
  // Default search parameters for MembershipType
  const defaultSearchParams: IMembershipTypeSearchRequest = {
    keywords: '',
    sortDirection: SortDirection.ASCENDING,
    sortField: 'name', // Default sort by name
    pageSize: 20,
    page: 1,
    filters: {
      categoryCode: '',
      classCode: '',
      membershiptypeId: '',
      name: '',
      description: ''
    }
  };

  // Hooks and state setup
  const api = useApi();
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [membershipTypePagedResults, setMembershipTypePagedResults] =
    useState<IPagedResult<IMembershipTypeDto[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] =
    useState<IMembershipTypeSearchRequest>(defaultSearchParams);

  // Search function to fetch MembershipType data from the API
  const search = useCallback(async (): Promise<void> => {
    // Only prevent search if requireKeywords is true and no keywords provided
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.keywords)) {
      setMembershipTypePagedResults(emptySearchResults);
      return;
    }

    setLoadingSearch(true);

    // Build query parameters
    const filters = JSON.stringify(searchParameters.filters);
    const queryParams = [];
    queryParams.push(`page=${searchParameters.page}`);
    queryParams.push(`pageSize=${searchParameters.pageSize}`);
    queryParams.push(`sortField=${searchParameters.sortField}`);
    queryParams.push(`sortDirection=${searchParameters.sortDirection.toString()}`);
    queryParams.push(`keywords=${encodeURIComponent(searchParameters.keywords)}`);
    queryParams.push(`filters=${encodeURIComponent(filters)}`);

    // API endpoint for MembershipType (adjust based on your backend route)
    const endpoint = `/membership-types?${queryParams.join('&')}`;
    const apiResponse = await api.get<IPagedResult<IMembershipTypeDto[]>>(endpoint);

    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      setMembershipTypePagedResults(apiResponse.response);
    } else {
      // Handle API failure by resetting to empty results
      setMembershipTypePagedResults(emptySearchResults);
    }
    setLoadingSearch(false);
  }, [api, searchParameters, requireKeywords]);

  // Reset search parameters to default
  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
  };

  // Change the current page
  const changePage = (page: number) => {
    setSearchParameters({ ...searchParameters, page: page });
  };

  // Change sorting field and direction
  const changeSort = (sortField: string) => {
    let sortDirection: SortDirection;
    if (searchParameters.sortField === sortField) {
      // Same field clicked - toggle direction
      sortDirection =
        (searchParameters.sortDirection as SortDirection) === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING;
    } else {
      // Different field clicked - start with ascending
      sortDirection = SortDirection.ASCENDING;
    }

    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection
    });
  };

  // Handle selection of a MembershipType
  const selectMembershipType = (membershipTypeId: string) => {
    setShowSearch(false);
    resetSearch();
    onChange(membershipTypeId);
  };

  // Check if there are valid search parameters
  const hasSearchParametersCheck = () => {
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.keywords)) return false;
    return !Helpers.isNullOrWhiteSpace(searchParameters.keywords);
  };

  // Debounced search for handling input changes (e.g., keywords or filters)
  const debouncedMembershipTypeSearch = useRef(
    debounce((keywords: string) => {
      setSearchParameters((prev) => ({ ...prev, page: 1, keywords: keywords }));
    }, 300)
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => debouncedMembershipTypeSearch.cancel();
  }, [debouncedMembershipTypeSearch]);

  // Trigger search when searchParameters change
  useEffect(() => {
    void search();
  }, [search, searchParameters]);

  // Return the hook's API
  return {
    loadingSearch,
    searchParameters,
    membershipTypePagedResults,
    debouncedMembershipTypeSearch,
    showSearch,
    hasSearchParameters: hasSearchParametersCheck(),
    setShowSearch,
    selectMembershipType,
    changePage,
    changeSort
  };
};

export default useMembershipTypeLookup;
